import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { Check, X, Zap } from "lucide-react";
import { useState, useEffect } from "react";

const comparisonData = [
  {
    feature: "Real Offers (24h)",
    description: "Get genuine offers within 24 hours",
    rsc: true,
    ebay: false,
    facebook: false,
  },
  {
    feature: "Zero Hassle",
    description: "Streamlined process with no complications",
    rsc: true,
    ebay: false,
    facebook: false,
  },
  {
    feature: "Faster Payouts",
    description: "Quick and reliable payment processing",
    rsc: "48 Hours After Delivery",
    ebay: "Varies",
    facebook: "Uncertain",
  },
  {
    feature: "Guaranteed Sales",
    description: "Your items will sell, guaranteed",
    rsc: true,
    ebay: false,
    facebook: false,
  },
  {
    feature: "Seller Protection",
    description: "Complete coverage for your transactions",
    rsc: true,
    ebay: "Limited",
    facebook: false,
  },
];

const ComparisonCell = ({ value, isVisible }) => {
  if (typeof value === "boolean") {
    return value ? (
      <Box
        sx={{
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? "scale(1)" : "scale(0.5)",
          transition: "all 0.5s ease",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Check size={24} className="text-emerald-500" />
      </Box>
    ) : (
      <Box
        sx={{
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? "scale(1)" : "scale(0.5)",
          transition: "all 0.5s ease",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <X size={24} className="text-red-500" />
      </Box>
    );
  }
  return (
    <Typography
      variant="body2"
      sx={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(20px)",
        transition: "all 0.5s ease",
      }}
    >
      {value}
    </Typography>
  );
};

export default function Comparison() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const element = document.getElementById("comparison-section");
    if (element) {
      observer.observe(element);
    }

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, []);

  return (
    <Box
      id="comparison-section"
      sx={{
        py: 12,
        background: "linear-gradient(180deg, #101010 0%, #1e1e1e 100%)",
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "1px",
          background:
            "linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)",
        },
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            mb: 2,
          }}
        >
          <Zap size={32} className="text-[#00ADB5]" />
          <Typography
            variant="h2"
            align="center"
            sx={{
              background: "linear-gradient(45deg, #ffffff, #00ADB5)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              fontSize: { xs: "2rem", md: "2.5rem" },
              fontWeight: 800, // Increased font weight
            }}
          >
            Why Choose RSC?
          </Typography>
        </Box>
        <Typography
          variant="h5"
          align="center"
          sx={{
            mb: 8,
            color: "text.secondary",
            maxWidth: "800px",
            mx: "auto",
            fontSize: { xs: "1rem", md: "1.25rem" },
            lineHeight: 1.6,
          }}
        >
          Compare our features and see why leading businesses trust RSC for
          their supply chain needs
        </Typography>

        <Box
          sx={{
            width: "100%",
            overflowX: "auto", // Enable horizontal scrolling
            WebkitOverflowScrolling: "touch", // Smooth scrolling on iOS
            "&::-webkit-scrollbar": {
              height: "8px",
            },
            "&::-webkit-scrollbar-track": {
              background: "rgba(255, 255, 255, 0.05)",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "rgba(255, 255, 255, 0.2)",
              borderRadius: "4px",
              "&:hover": {
                background: "rgba(255, 255, 255, 0.3)",
              },
            },
          }}
        >
          <TableContainer
            component={Paper}
            sx={{
              bgcolor: "transparent",
              backgroundImage:
                "linear-gradient(145deg, rgba(30,30,30,0.95), rgba(16,16,16,0.95))",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: "24px",
              overflow: "hidden",
              boxShadow: "0 4px 30px rgba(0, 0, 0, 0.1)",
              "& .MuiTableCell-root": {
                borderColor: "rgba(255,255,255,0.05)",
              },
              transform: isVisible ? "translateY(0)" : "translateY(40px)",
              opacity: isVisible ? 1 : 0,
              transition: "all 0.8s cubic-bezier(0.17, 0.55, 0.55, 1)",
              minWidth: { xs: "800px", md: "100%" }, // Set minimum width for mobile scrolling
            }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 700, // Increased font weight
                      fontSize: { xs: "0.9rem", md: "1.1rem" },
                      color: "white",
                      bgcolor: "rgba(0,0,0,0.4)",
                      py: 3,
                      width: "30%", // Fixed width for feature column
                    }}
                  >
                    Feature
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 700, // Increased font weight
                      fontSize: { xs: "0.9rem", md: "1.1rem" },
                      color: "#00ADB5",
                      bgcolor: "rgba(0,0,0,0.4)",
                      py: 3,
                      width: "23.33%", // Equal width for other columns
                    }}
                  >
                    RSC
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontSize: { xs: "0.9rem", md: "1.1rem" },
                      color: "white",
                      bgcolor: "rgba(0,0,0,0.4)",
                      py: 3,
                      width: "23.33%", // Equal width for other columns
                    }}
                  >
                    eBay
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontSize: { xs: "0.9rem", md: "1.1rem" },
                      color: "white",
                      bgcolor: "rgba(0,0,0,0.4)",
                      py: 3,
                      width: "23.33%", // Equal width for other columns
                    }}
                  >
                    Facebook Marketplace
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {comparisonData.map((row, index) => (
                  <TableRow
                    key={row.feature}
                    sx={{
                      "&:nth-of-type(odd)": {
                        bgcolor: "rgba(255,255,255,0.02)",
                      },
                      "&:hover": {
                        bgcolor: "rgba(0,173,181,0.05)",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    <TableCell
                      component="th"
                      scope="row"
                      sx={{
                        py: 3,
                        px: 4,
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 600, // Increased font weight
                          color: "white",
                          mb: 0.5,
                        }}
                      >
                        {row.feature}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                          display: { xs: "none", md: "block" },
                        }}
                      >
                        {row.description}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ py: 3 }}>
                      <ComparisonCell value={row.rsc} isVisible={isVisible} />
                    </TableCell>
                    <TableCell align="center" sx={{ py: 3 }}>
                      <ComparisonCell value={row.ebay} isVisible={isVisible} />
                    </TableCell>
                    <TableCell align="center" sx={{ py: 3 }}>
                      <ComparisonCell
                        value={row.facebook}
                        isVisible={isVisible}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Container>
    </Box>
  );
}
